import { useState, useEffect, useRef } from 'react';
import { Settings, Check, ChevronDown } from 'lucide-react';
import { useAuth, llmConfigApi } from '@umd-dit-ai-workspace/workspaces-component';
import type { LLMConfig } from '@umd-dit-ai-workspace/workspaces-component';
import { configApi } from '../api/config';

interface ModelData {
  id: string;
  name: string;
  modelId: string;
  description: string;
  inputCost: string;
  outputCost: string;
  visionCapable?: boolean;
  recommended?: boolean;
}

export function LLMConfigurationView() {
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [selectedModel, setSelectedModel] = useState('bedrock_claude_45_sonnet');
  const [models, setModels] = useState<ModelData[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth();

  // Load available models from API
  useEffect(() => {
    async function loadModels() {
      try {
        setIsLoadingModels(true);
        const configs = await llmConfigApi.listLLMConfigs({ active_only: true });

        // Transform API response to ModelData format
        const transformedModels: ModelData[] = configs.map((config: LLMConfig) => ({
          id: config.model_name,
          name: config.display_name,
          modelId: config.model_id,
          description: `${config.provider} model`,
          inputCost: `$${config.input_token_cost.toFixed(2)}`,
          outputCost: `$${config.output_token_cost.toFixed(2)}`,
          visionCapable: config.vision_supported,
          recommended: config.model_name === 'bedrock_claude_45_sonnet'
        }));

        setModels(transformedModels);
        setError(null);
      } catch (err) {
        console.error('Failed to load models:', err);
        setError('Failed to load available models');
      } finally {
        setIsLoadingModels(false);
      }
    }

    loadModels();
  }, []);

  // Load current workspace config on mount
  useEffect(() => {
    async function loadConfig() {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const config = await configApi.getWorkspaceConfig(selectedOrganizationId, selectedWorkspaceId);
        setSelectedModel(config.default_llm_model || 'bedrock_claude_45_sonnet');
        setError(null);
      } catch (err) {
        console.error('Failed to load workspace config:', err);
        setError('Failed to load configuration');
        // Keep default model selection on error
      } finally {
        setIsLoading(false);
      }
    }

    loadConfig();
  }, [selectedOrganizationId, selectedWorkspaceId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowModelDropdown(false);
      }
    }

    if (showModelDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showModelDropdown]);

  const selectedModelData = models.find(m => m.id === selectedModel);

  const handleSave = async () => {
    if (!selectedOrganizationId || !selectedWorkspaceId) {
      setError('No workspace selected');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await configApi.updateWorkspaceConfig(
        selectedOrganizationId,
        selectedWorkspaceId,
        { default_llm_model: selectedModel }
      );
      setSuccessMessage('Model configuration saved successfully!');
      
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      console.error('Failed to save workspace config:', err);
      setError('Failed to save configuration. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSelectedModel('bedrock_claude_45_sonnet');
  };

  if (isLoading || isLoadingModels) {
    return (
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-[#e21833] rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Settings className="w-5 h-5 text-gray-700" />
        <h3 className="text-lg font-bold text-gray-800">LLM Model Configuration</h3>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Select the AI model used for all document analysis and comparison tasks in this workspace
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Model Dropdown */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Default Model
        </label>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowModelDropdown(!showModelDropdown)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
          >
            <div className="flex flex-col items-start">
              <span className="font-medium text-gray-900">{selectedModelData?.name}</span>
              <span className="text-xs text-gray-500">{selectedModelData?.description}</span>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showModelDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showModelDropdown && (
            <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
              {models.map(model => (
                <button
                  key={model.id}
                  onClick={() => {
                    setSelectedModel(model.id);
                    setShowModelDropdown(false);
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg transition-colors text-left"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{model.name}</span>
                      {model.recommended && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                          Recommended
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{model.description}</div>
                    <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-3">
                      <span>In: {model.inputCost}/1K</span>
                      <span>•</span>
                      <span>Out: {model.outputCost}/1K</span>
                    </div>
                  </div>
                  {selectedModel === model.id && (
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <button 
          onClick={handleReset}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
        >
          Reset
        </button>
        <button 
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 text-white bg-[#e21833] rounded-lg hover:bg-[#c41429] disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              Saving...
            </>
          ) : (
            'Save Configuration'
          )}
        </button>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-center gap-2">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}
    </div>
  );
}
